/**
 * Axis colours of the 2D and 3D scatters: every axis has a colour for its low and its high end; a dot takes
 * the colour of its place on each axis and the axes' colours are mixed. Mixing is linear: the weighted
 * average of the channel values (owner's choice, spec 0.49; it replaced root-mean-square mixing).
 */

export type RGB = [number, number, number];

export function parseHex(hex: string): RGB | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function toHex([r, g, b]: RGB): string {
  const c = (v: number) =>
    Math.round(Math.min(255, Math.max(0, v)))
      .toString(16)
      .padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Linear mixing of colours with weights (they need not add up to 1). */
export function mixLinear(colours: RGB[], weights?: number[]): RGB {
  const w = weights ?? colours.map(() => 1);
  const total = w.reduce((s, x) => s + x, 0) || 1;
  const out: RGB = [0, 0, 0];
  for (let ch = 0; ch < 3; ch++) {
    let acc = 0;
    colours.forEach((c, i) => (acc += (w[i] / total) * c[ch]));
    out[ch] = acc;
  }
  return out;
}

export interface AxisColours {
  low: string;
  high: string;
}

/** The colour of a dot: on each axis between low and high by `t` (0..1), then the axes mixed equally. */
export function dotColour(axes: AxisColours[], ts: number[]): string | null {
  const per: RGB[] = [];
  axes.forEach((a, i) => {
    const lo = parseHex(a.low);
    const hi = parseHex(a.high);
    if (!lo || !hi) return;
    const t = Math.min(1, Math.max(0, Number.isFinite(ts[i]) ? ts[i] : 0));
    per.push(mixLinear([lo, hi], [1 - t, t]));
  });
  return per.length ? toHex(mixLinear(per)) : null;
}

/** Defaults: a quiet grey at the low end of every axis, a distinct hue at the high end. */
export const AXIS_DEFAULTS = {
  x: { low: "#9aa5b1", high: "#2a78d6" },
  y: { low: "#9aa5b1", high: "#d6762a" },
  z: { low: "#9aa5b1", high: "#2f9e57" },
} as const;
